import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Copy, Minus } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { MissedPunches } from "../components/MissedPunches";
import { EvvLadder } from "../components/EvvLadder";

const pctCls = (p, floor, target) =>
  p == null ? "text-stone-400" : p >= target ? "text-[#1e8449]" : p >= floor ? "text-[#8a5d14]" : "text-[#C0392B]";

const FOLLOW_UPS = ["Talked to caregiver", "Retrained", "Documented warning", "Not their fault — excused", "Other"];

const CALL_IN_NOTE = `Reminder: if you can't clock in or out, CALL THE AGENCY right away — don't wait. When you call us in real time, we can fix the visit so it doesn't count against your compliance record. Missed punches without a call-in do count.`;

function OvernightCheckins() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/overnight-checkin?days=30").then((r) => setRows(r.data.checkins || [])).catch((e) => { toast.error(formatApiError(e)); setRows([]); });
  }, []);
  if (rows === null) return <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  if (!rows.length) return <p className="rounded-lg border border-stone-200 bg-white p-6 text-center text-sm text-stone-400" data-testid="overnight-empty">No overnight check-ins in the last 30 days. Staff check in from their dashboard.</p>;
  return (
    <div className="overflow-hidden rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="overnight-table">
      <table className="w-full text-left text-sm">
        <thead><tr className="border-b border-stone-200 bg-secondary/60 text-xs uppercase tracking-wide text-stone-500">
          <th className="px-5 py-3 font-medium">Caregiver</th><th className="px-5 py-3 font-medium">Client</th><th className="px-5 py-3 font-medium">Checked in (ET)</th>
        </tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className="border-b border-stone-100 last:border-0" data-testid={`overnight-row-${c.id}`}>
              <td className="px-5 py-3 font-medium text-stone-800">{c.name}</td>
              <td className="px-5 py-3 text-stone-600">{c.client_name || "—"}</td>
              <td className="px-5 py-3 text-stone-600">{new Date(c.punched_at).toLocaleString("en-US", { timeZone: "America/New_York", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EvvLetters({ floor }) {
  const [letters, setLetters] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState("");

  const load = () => api.get("/evv/letters").then((r) => setLetters(r.data.letters || [])).catch((e) => { toast.error(formatApiError(e)); setLetters([]); });
  useEffect(() => { load(); }, []);

  const scan = async () => {
    setBusy("scan");
    try {
      const { data } = await api.post("/evv/letters/scan");
      toast.success(data.count
        ? `Drafted ${data.count} letter(s): ${data.created.join(", ")}`
        : "No one eligible — eligibility needs under-floor compliance in at least 2 separate periods, with the latest period still under the floor.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };
  const save = async (l) => {
    setBusy(l.id);
    try {
      await api.put(`/evv/letters/${l.id}`, { body: edits[l.id] ?? l.body });
      toast.success("Draft saved");
      setEdits((e) => { const n = { ...e }; delete n[l.id]; return n; });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };
  const approve = async (l) => {
    setBusy(l.id);
    try {
      if (edits[l.id] != null && edits[l.id] !== l.body) await api.put(`/evv/letters/${l.id}`, { body: edits[l.id] });
      await api.post(`/evv/letters/${l.id}/approve`);
      toast.success(`Approved — ${l.employee_name} can now sign it in My Profile`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };
  const del = async (l) => {
    if (!window.confirm(`Delete the draft letter for ${l.employee_name}?`)) return;
    setBusy(l.id);
    try { await api.delete(`/evv/letters/${l.id}`); toast.success("Draft deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  if (letters === null) return <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  const STATUS_BADGE = { draft: "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]", approved: "border-primary/40 bg-primary/10 text-primary", signed: "border-[#27AE60]/40 bg-[#27AE60]/10 text-[#1e8449]" };
  return (
    <div className="space-y-4" data-testid="evv-letters">
      <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-heading text-sm font-semibold text-stone-900">EVV acknowledgement letters</p>
            <p className="mt-0.5 max-w-2xl text-xs text-stone-500">
              A draft is created for staff under {floor}% in at least 2 separate reporting periods with the latest period still under the floor —
              a single bad month or a one-day issue never triggers one. Edit the draft, approve it, and the employee signs it in My Profile.
            </p>
          </div>
          <Button onClick={scan} disabled={busy === "scan"} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="evv-letters-scan">
            {busy === "scan" ? "Scanning…" : "Scan for eligible staff"}
          </Button>
        </div>
      </div>
      {letters.length === 0 && <p className="rounded-lg border border-stone-200 bg-white p-6 text-center text-sm text-stone-400" data-testid="evv-letters-empty">No letters yet. Scan for eligible staff, or use the Letter button on a caregiver row in the Analysis tab.</p>}
      {letters.map((l) => (
        <div key={l.id} className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid={`evv-letter-${l.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <Link to={`/team/${l.uid}`} className="text-sm font-semibold text-stone-900 hover:text-primary hover:underline">{l.employee_name}</Link>
              <p className="text-xs text-stone-400">{(l.periods || []).map((p) => `${p.month}: ${p.pct}%`).join(" · ")}</p>
            </div>
            <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_BADGE[l.status] || ""}`} data-testid={`evv-letter-status-${l.id}`}>
              {l.status === "draft" ? "Draft — edit & approve" : l.status === "approved" ? "Approved — awaiting employee signature" : `Signed by ${l.signed_name} on ${String(l.signed_at || "").slice(0, 10)}`}
            </span>
          </div>
          {l.status === "draft" ? (
            <div className="mt-3">
              <textarea rows={12} value={edits[l.id] ?? l.body} onChange={(e) => setEdits((cur) => ({ ...cur, [l.id]: e.target.value }))}
                className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 font-mono text-xs text-stone-700" data-testid={`evv-letter-body-${l.id}`} />
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => save(l)} disabled={busy === l.id} className="border-stone-300" data-testid={`evv-letter-save-${l.id}`}>Save draft</Button>
                <Button size="sm" onClick={() => approve(l)} disabled={busy === l.id} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`evv-letter-approve-${l.id}`}>Approve — send to employee for signature</Button>
                <Button size="sm" variant="outline" onClick={() => del(l)} disabled={busy === l.id} className="border-[#C0392B]/40 text-[#C0392B] hover:bg-[#C0392B] hover:text-white" data-testid={`evv-letter-delete-${l.id}`}>Delete</Button>
              </div>
            </div>
          ) : (
            <pre className="mt-3 max-h-48 overflow-y-auto whitespace-pre-wrap rounded-md bg-stone-50 p-3 text-xs text-stone-600" data-testid={`evv-letter-view-${l.id}`}>{l.body}</pre>
          )}
        </div>
      ))}
    </div>
  );
}

export default function EvvPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [agency, setAgency] = useState([]);
  const [tab, setTab] = useState("analysis");
  const [showAll, setShowAll] = useState(false);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState("");
  const [payerVisits, setPayerVisits] = useState(null);
  const [reportFiles, setReportFiles] = useState([]);
  const [repFile, setRepFile] = useState(null);
  const [repMonth, setRepMonth] = useState("");
  const [repPeriod, setRepPeriod] = useState("month");
  const [repWeek, setRepWeek] = useState("");
  const [repBusy, setRepBusy] = useState(false);

  const load = () => {
    api.get("/evv/analysis").then((r) => setData(r.data)).catch((e) => { toast.error(formatApiError(e)); setData({ monthly: [], quarterly: [], caregivers: [] }); });
    api.get("/evv/agency").then((r) => setAgency(r.data || [])).catch(() => setAgency([]));
    api.get("/evv/visits-by-payer").then((r) => setPayerVisits(r.data)).catch(() => setPayerVisits({ months: [], payers: [] }));
    api.get("/evv/report-files").then((r) => setReportFiles(r.data || [])).catch(() => setReportFiles([]));
  };

  const clearAll = async () => {
    const typed = window.prompt("This deletes ALL imported EVV report data — payer reports, every caregiver's monthly + weekly numbers, and the ladder — so you can re-upload clean. Signed acknowledgement letters are kept.\n\nType DELETE ALL EVV REPORTS to confirm:");
    if (typed === null) return;
    try {
      const { data } = await api.post("/evv/clear-all", { confirm: typed });
      toast.success(`Cleared: ${data.payer_rows} payer rows, ${data.files} files, ${data.staff_records_cleared} staff records`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadReport = async () => {
    if (!repFile) { toast.error("Choose the report file first"); return; }
    if (repPeriod === "week" && !repWeek) { toast.error("Pick the week the report covers (any day that week — it snaps to Monday)"); return; }
    setRepBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", repFile);
      const qs = repPeriod === "week" ? `?week=${repWeek}` : repMonth ? `?month=${repMonth}` : "";
      const { data } = await api.post(`/evv/upload${qs}`, fd);
      toast.success(`Imported ${data.payer_rows || 0} payer row(s) · ${data.matched} caregiver row(s) matched${data.unmatched?.length ? ` · ${data.unmatched.length} unmatched` : ""}${data.week ? ` · week of ${data.week}` : ""}`);
      setRepFile(null);
      setRepMonth("");
      setRepWeek("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setRepBusy(false);
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const floor = data?.floor ?? 85;
  const target = data?.target ?? 90;

  const caregivers = useMemo(() => {
    let list = data?.caregivers || [];
    if (!showAll) list = list.filter((c) => c.active && !["inactive", "terminated"].includes((c.roster_status || "").toLowerCase()));
    return [...list].sort((a, b) => (a.adjusted_pct ?? 999) - (b.adjusted_pct ?? 999));
  }, [data, showAll]);

  const latest = data?.monthly?.length ? data.monthly[data.monthly.length - 1] : null;
  const belowFloor = caregivers.filter((c) => (c.adjusted_pct ?? 100) < floor).length;

  const setEdit = (uid, field, val) => setEdits((e) => ({ ...e, [uid]: { ...(e[uid] || {}), [field]: val } }));
  const rowVal = (c, field) => (edits[c.uid]?.[field] ?? (field === "excused_exceptions" ? c.excused : c[field]));

  const saveHandling = async (c) => {
    setSaving(c.uid);
    try {
      await api.post("/evv/handling", {
        uid: c.uid, month: c.month,
        excused_exceptions: Number(rowVal(c, "excused_exceptions") || 0),
        follow_up: rowVal(c, "follow_up") || "",
        note: rowVal(c, "note") || "",
      });
      toast.success(`Saved handling for ${c.name}`);
      setEdits((e) => { const n = { ...e }; delete n[c.uid]; return n; });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving("");
  };

  const [letterBusy, setLetterBusy] = useState("");
  const draftLetter = async (c) => {
    setLetterBusy(c.uid);
    try {
      const { data } = await api.post("/evv/letters/generate", { uid: c.uid });
      toast.success(`Draft letter created for ${c.name}${data.eligible ? "" : " — under the floor in only 1 period, review before approving"}`);
      setTab("letters");
    } catch (e) { toast.error(formatApiError(e)); }
    setLetterBusy("");
  };

  if (!isAdmin) {
    return <div className="py-16 text-center text-sm text-stone-500" data-testid="evv-admin-only">EVV analysis is available to admins only.</div>;
  }

  return (
    <div className="space-y-6" data-testid="evv-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-stone-900" data-testid="evv-title">EVV Compliance</h1>
        <p className="mt-1 text-sm text-stone-500">Monthly and quarterly caregiver EVV analysis. Agency floor {floor}% · target {target}%. Exceptions that aren't the caregiver's fault can be excused per month.</p>
      </div>

      <div className="flex gap-1 rounded-lg border border-stone-200 bg-white p-1 shadow-sm" data-testid="evv-tabs">
        {[["analysis", "Analysis"], ["ladder", "Weekly ladder"], ["missed", "Missed punches"], ["overnight", "Overnight check-ins"], ["letters", "Acknowledgement letters"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${tab === k ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-accent"}`}
            data-testid={`evv-tab-${k}`}>{label}</button>
        ))}
      </div>

      {tab === "ladder" ? (
        <EvvLadder />
      ) : tab === "missed" ? (
        <MissedPunches />
      ) : tab === "overnight" ? (
        <OvernightCheckins />
      ) : tab === "letters" ? (
        <EvvLetters floor={floor} />
      ) : data === null ? (
        <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[
              ["Latest month agency rate", latest ? `${latest.pct}%` : "—", "evv-latest-pct", pctCls(latest?.pct, floor, target)],
              [`Caregivers below ${floor}% (adjusted)`, belowFloor, "evv-below-floor", belowFloor > 0 ? "text-[#C0392B]" : "text-[#1e8449]"],
              ["Missed visits (latest)", latest?.missed ?? "—", "evv-missed", "text-stone-900"],
              ["Exceptions (latest)", latest?.exceptions ?? "—", "evv-exceptions", "text-stone-900"],
            ].map(([label, val, testid, cls]) => (
              <div key={testid} className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid={testid}>
                <p className={`font-heading text-2xl font-bold ${cls}`}>{val}</p>
                <p className="text-xs text-stone-500">{label}</p>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="evv-report-upload">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-0 flex-1">
                <h2 className="font-heading text-lg font-semibold text-stone-900">Report upload</h2>
                <p className="mt-0.5 text-xs text-stone-500">Weekly: the per-employee EVV report (feeds the Weekly ladder + profile windows). Monthly: the insurer compliance report (feeds Payer reports). Caregiver rows feed the monthly analysis. The Monday reminder email tracks what arrived.</p>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-stone-500">Report covers</label>
                <select value={repPeriod} onChange={(e) => setRepPeriod(e.target.value)} className="h-10 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="evv-report-period">
                  <option value="month">A month</option>
                  <option value="week">A week (Mon–Sun)</option>
                </select>
              </div>
              {repPeriod === "week" ? (
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Week (snaps to Monday)</label>
                  <input type="date" value={repWeek} onChange={(e) => setRepWeek(e.target.value)} className="h-10 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="evv-report-week" />
                </div>
              ) : (
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Month (optional)</label>
                  <input type="month" value={repMonth} onChange={(e) => setRepMonth(e.target.value)} className="h-10 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="evv-report-month" />
                </div>
              )}
              <div>
                <label className="mb-1 block text-xs font-medium text-stone-500">Report file</label>
                <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setRepFile(e.target.files?.[0] || null)} className="block w-56 text-xs text-stone-600 file:mr-2 file:rounded-full file:border-0 file:bg-stone-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold hover:file:bg-stone-200" data-testid="evv-report-file" />
              </div>
              <Button onClick={uploadReport} disabled={repBusy} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="evv-report-upload-btn">
                {repBusy ? "Uploading…" : "Upload report"}
              </Button>
            </div>
            {reportFiles.length > 0 && (
              <div className="mt-3 border-t border-stone-100 pt-3" data-testid="evv-report-files">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">Uploaded report files</p>
                <ul className="mt-1 space-y-1">
                  {reportFiles.map((f) => (
                    <li key={f.id} className="flex flex-wrap items-center gap-2 text-xs text-stone-600" data-testid={`evv-report-file-${f.id}`}>
                      <a href={`${api.defaults.baseURL}/evv/report-files/${f.id}/file`} target="_blank" rel="noreferrer"
                        className="font-medium text-primary hover:underline" data-testid={`evv-report-file-view-${f.id}`}>{f.filename}</a>
                      <span className="text-stone-400">{f.kind} · {f.period} · {String(f.uploaded_at || "").slice(0, 10)} by {f.uploaded_by} · {f.matched} caregiver row(s){f.payer_rows ? ` · ${f.payer_rows} payer row(s)` : ""}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-3 border-t border-stone-100 pt-2 text-right">
              <button onClick={clearAll} className="text-[11px] font-medium text-stone-400 hover:text-[#C0392B]" data-testid="evv-clear-all">
                Clear all EVV report data…
              </button>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm lg:col-span-2" data-testid="evv-payer-reports">
            <h2 className="font-heading text-lg font-semibold text-stone-900">Payer reports <span className="text-xs font-normal text-stone-400">— monthly insurer compliance report (trends + visit counts)</span></h2>
            {agency.length === 0 ? (
              <p className="mt-2 text-xs text-stone-400">No insurer reports yet — upload the monthly payer file above.</p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-stone-200 text-[10px] uppercase tracking-wide text-stone-400">
                      <th className="py-1 pr-3">Payer</th><th className="py-1 pr-3">Month</th>
                      <th className="py-1 pr-3 text-right">Compliant</th><th className="py-1 pr-3 text-right">Exceptions</th>
                      <th className="py-1 pr-3 text-right">Visits</th><th className="py-1 pr-3 text-right">Month %</th>
                      <th className="py-1 text-right">YTD %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agency.filter((d) => (d.confirmed || 0) > 0).slice(0, 12).map((d, i) => {
                      const prev = agency.find((x) => x.payer === d.payer && x.period < d.period && (x.confirmed || 0) > 0);
                      const delta = prev ? Math.round((d.pct - prev.pct) * 100) / 100 : null;
                      return (
                        <tr key={i} className="border-b border-stone-100" data-testid={`evv-payer-row-${i}`}>
                          <td className="py-1.5 pr-3 font-medium text-stone-800">{d.payer}</td>
                          <td className="py-1.5 pr-3 text-stone-500">{d.period}</td>
                          <td className="py-1.5 pr-3 text-right text-stone-700">{d.compliant}</td>
                          <td className="py-1.5 pr-3 text-right text-stone-700">{d.exceptions}</td>
                          <td className="py-1.5 pr-3 text-right text-stone-700">{d.confirmed}</td>
                          <td className={`py-1.5 pr-3 text-right font-semibold ${d.pct >= 90 ? "text-[#1e8449]" : "text-[#8a5d14]"}`}>
                            {d.pct}%{delta !== null && <span className={`ml-1 text-[10px] ${delta >= 0 ? "text-[#1e8449]" : "text-[#C0392B]"}`}>{delta >= 0 ? "▲" : "▼"}{Math.abs(delta)}</span>}
                          </td>
                          <td className="py-1.5 text-right text-stone-700">{d.ytd_pct ?? "—"}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm lg:col-span-2" data-testid="evv-visits-by-payer">
            <h2 className="font-heading text-lg font-semibold text-stone-900">Visits by payer <span className="text-xs font-normal text-stone-400">— actual worked visits from the visit log{payerVisits?.months?.length ? `, last ${payerVisits.months.length} months` : ""}</span></h2>
            {payerVisits === null ? (
              <div className="flex justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
            ) : payerVisits.payers.length === 0 ? (
              <p className="mt-2 text-xs text-stone-400">No visits in this window yet.</p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-stone-200 text-[10px] uppercase tracking-wide text-stone-400">
                      <th className="py-1 pr-3">Payer</th>
                      {payerVisits.months.map((m) => <th key={m} className="py-1 pr-3 text-right">{m}</th>)}
                      <th className="py-1 pr-3 text-right">Total visits</th>
                      <th className="py-1 text-right">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payerVisits.payers.map((p, i) => (
                      <tr key={p.payer} className="border-b border-stone-100" data-testid={`evv-payer-visits-row-${i}`}>
                        <td className="py-1.5 pr-3 font-medium text-stone-800">{p.payer}</td>
                        {payerVisits.months.map((m) => (
                          <td key={m} className="py-1.5 pr-3 text-right text-stone-600">{p.by_month[m] ?? <span className="text-stone-300">—</span>}</td>
                        ))}
                        <td className="py-1.5 pr-3 text-right font-semibold text-stone-800">{p.total}</td>
                        <td className="py-1.5 text-right text-stone-600">{p.hours}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="evv-monthly">
              <h2 className="font-heading text-lg font-semibold text-stone-900">Agency rate by month</h2>
              <div className="mt-3 space-y-2">
                {data.monthly.map((m, i) => (
                  <div key={m.month} className="flex items-center gap-2 text-sm" data-testid={`evv-month-${i}`}>
                    <span className="w-16 text-xs text-stone-500">{m.month}</span>
                    <div className="relative h-4 flex-1 rounded-sm bg-stone-100">
                      <div className={`h-4 rounded-sm ${(m.pct ?? 0) >= target ? "bg-[#27AE60]" : (m.pct ?? 0) >= floor ? "bg-[#B7791F]" : "bg-[#C0392B]"}`}
                        style={{ width: `${Math.min(100, m.pct ?? 0)}%` }} />
                      <div className="absolute top-0 h-4 w-px bg-stone-400" style={{ left: `${floor}%` }} title={`floor ${floor}%`} />
                      <div className="absolute top-0 h-4 w-px bg-stone-700" style={{ left: `${target}%` }} title={`target ${target}%`} />
                    </div>
                    <span className={`w-14 text-right text-xs font-medium ${pctCls(m.pct, floor, target)}`}>{m.pct ?? "—"}%</span>
                  </div>
                ))}
                {data.monthly.length === 0 && <p className="text-sm text-stone-400">Upload an EVV report on the Team page to start monthly tracking.</p>}
              </div>
            </div>
            <div className="space-y-4">
              <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="evv-quarterly">
                <h2 className="font-heading text-lg font-semibold text-stone-900">Quarterly rollup</h2>
                <table className="mt-2 w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-stone-200 text-xs uppercase tracking-wide text-stone-500">
                      <th className="py-2 pr-3 font-medium">Quarter</th>
                      <th className="py-2 pr-3 font-medium">Visits</th>
                      <th className="py-2 pr-3 font-medium">Compliant</th>
                      <th className="py-2 font-medium">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.quarterly.map((q, i) => (
                      <tr key={q.quarter} className="border-b border-stone-100 last:border-0" data-testid={`evv-quarter-${i}`}>
                        <td className="py-2 pr-3 text-stone-700">{q.quarter}</td>
                        <td className="py-2 pr-3 text-stone-600">{q.visits}</td>
                        <td className="py-2 pr-3 text-stone-600">{q.verified}</td>
                        <td className={`py-2 font-medium ${pctCls(q.pct, floor, target)}`}>{q.pct ?? "—"}%</td>
                      </tr>
                    ))}
                    {data.quarterly.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-sm text-stone-400">No data yet.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-4" data-testid="evv-callin-note">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">Caregiver call-in reinforcement</p>
                <p className="mt-1 text-sm text-stone-700">{CALL_IN_NOTE}</p>
                <Button variant="outline" className="mt-2 h-8 border-primary/40 text-xs text-primary hover:bg-primary hover:text-white"
                  onClick={() => { navigator.clipboard.writeText(CALL_IN_NOTE); toast.success("Copied — paste into a text or email"); }} data-testid="evv-copy-callin">
                  <Copy size={13} className="mr-1" /> Copy reminder
                </Button>
              </div>
            </div>
          </div>

          <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-heading text-lg font-semibold text-stone-900">Caregivers — latest period, worst first</h2>
              <label className="flex items-center gap-2 text-xs text-stone-500" data-testid="evv-show-all-label">
                <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="evv-show-all" />
                include inactive / former staff
              </label>
            </div>
            <div className="overflow-x-auto rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="evv-table">
              <table className="w-full min-w-[1000px] text-left text-sm">
                <thead>
                  <tr className="border-b border-stone-200 bg-secondary/60 text-xs uppercase tracking-wide text-stone-500">
                    <th className="px-4 py-3 font-medium">Caregiver</th>
                    <th className="px-3 py-3 font-medium">Period</th>
                    <th className="px-3 py-3 font-medium">Visits</th>
                    <th className="px-3 py-3 font-medium">Compliance</th>
                    <th className="px-3 py-3 font-medium">Excused</th>
                    <th className="px-3 py-3 font-medium">Adjusted</th>
                    <th className="px-3 py-3 font-medium">Follow-up</th>
                    <th className="px-3 py-3 font-medium">Note</th>
                    <th className="px-3 py-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {caregivers.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-stone-400" data-testid="evv-empty">No EVV records yet.</td></tr>
                  )}
                  {caregivers.map((c, i) => {
                    const trend = c.prev_pct == null || c.pct == null ? null : c.pct - c.prev_pct;
                    const dirty = edits[c.uid] != null;
                    return (
                      <tr key={c.uid} className="border-b border-stone-100 last:border-0 align-middle" data-testid={`evv-row-${i}`}>
                        <td className="px-4 py-2">
                          <Link to={`/team/${c.uid}`} className="font-medium text-stone-900 hover:text-primary hover:underline" data-testid={`evv-caregiver-${i}`}>{c.name || c.email}</Link>
                          {c.roster_status && ["inactive", "terminated"].includes(c.roster_status.toLowerCase()) && (
                            <span className="ml-1.5 text-[10px] text-stone-400">roster: {c.roster_status.toLowerCase()}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-stone-500">{c.month}</td>
                        <td className="px-3 py-2 text-stone-600">{c.total}</td>
                        <td className={`px-3 py-2 font-medium ${pctCls(c.pct, floor, target)}`} data-testid={`evv-pct-${i}`}>
                          {c.pct ?? "—"}%
                          {trend != null && (
                            <span className={`ml-1 inline-flex items-center text-[10px] ${trend > 0 ? "text-[#1e8449]" : trend < 0 ? "text-[#C0392B]" : "text-stone-400"}`} data-testid={`evv-trend-${i}`}>
                              {trend > 0 ? <ArrowUp size={10} /> : trend < 0 ? <ArrowDown size={10} /> : <Minus size={10} />}
                              {trend > 0 ? "+" : ""}{trend.toFixed(1)}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" min="0" max={c.exceptions} value={rowVal(c, "excused_exceptions") ?? 0}
                            onChange={(e) => setEdit(c.uid, "excused_exceptions", e.target.value)}
                            className="h-8 w-16 rounded-md border border-stone-300 bg-white px-1.5 text-xs text-stone-700"
                            title={`of ${c.exceptions} exceptions`} data-testid={`evv-excused-${i}`} />
                          <span className="ml-1 text-[10px] text-stone-400">/ {c.exceptions}</span>
                          {c.mp_not_fault > 0 && <span className="ml-1 text-[10px] font-medium text-[#1e8449]" data-testid={`evv-mp-hint-${i}`}>· {c.mp_not_fault} form(s)</span>}
                        </td>
                        <td className={`px-3 py-2 font-medium ${pctCls(c.adjusted_pct, floor, target)}`} data-testid={`evv-adjusted-${i}`}>{c.adjusted_pct ?? "—"}%</td>
                        <td className="px-3 py-2">
                          <select value={rowVal(c, "follow_up") || ""} onChange={(e) => setEdit(c.uid, "follow_up", e.target.value)}
                            className="h-8 w-40 rounded-md border border-stone-300 bg-white px-1.5 text-xs text-stone-700" data-testid={`evv-followup-${i}`}>
                            <option value="">—</option>
                            {FOLLOW_UPS.map((f) => <option key={f} value={f}>{f}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input value={rowVal(c, "note") || ""} onChange={(e) => setEdit(c.uid, "note", e.target.value)}
                            placeholder="handling note…" className="h-8 w-44 rounded-md border border-stone-300 bg-white px-1.5 text-xs text-stone-700" data-testid={`evv-note-${i}`} />
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            {dirty && (
                              <Button onClick={() => saveHandling(c)} disabled={saving === c.uid} className="h-8 bg-primary px-3 text-xs text-primary-foreground hover:bg-stone-900" data-testid={`evv-save-${i}`}>
                                {saving === c.uid ? "Saving…" : "Save"}
                              </Button>
                            )}
                            {(c.adjusted_pct ?? 100) < floor && (
                              <Button variant="outline" onClick={() => draftLetter(c)} disabled={letterBusy === c.uid}
                                className="h-8 border-[#C0392B]/40 px-2 text-xs text-[#C0392B] hover:bg-[#C0392B] hover:text-white" data-testid={`evv-letter-${i}`}>
                                {letterBusy === c.uid ? "…" : "Letter"}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
